import type { FormElementProps, SelectProps } from '@magicschool/business-logic/forms';
import type { ExplicitToolConfig } from '@magicschool/business-logic/tools';
import type { ResponseErrorHandler } from '@magicschool/monkey-fetch';
import { toast } from '@magicschool/spellbook';
import type { Json, Tables, TablesInsert } from '@magicschool/supabase/types';
import isEqual from 'lodash-es/isEqual';
import pick from 'lodash-es/pick';
import type { CreateToolConfigResponse } from '@/app/api/admin/tool-configs/route';
import type { AdminToolResponse, AdminUpdateToolRequest } from '@/app/api/admin/tools/[slug]/route';
import { type ComponentKey, componentMapKeys } from '@/components/FormBuilder/util';
import { emptyAdminToolDetails, emptyTool, emptyToolConfig } from '@/features/backend-api/types';
import { createStoreSlice, type SetField } from '@/features/store/zustand';
import { ImmutableMap } from '@/util/immutable';
import { getBadgesFromPlainText, resolveIconCategoryId } from './utils';

/** The server's 409 says what it refused (a code-owned field, a stale version), so show its message instead of the generic one. */
const toastConflict: ResponseErrorHandler = async ({ response }) => {
  const data: { error: string } = await response.json();
  toast({ title: `Conflict detected. ${data.error}`, status: 'error' });
};

type EditToolErrorMap = {
  title?: string;
  slug?: string;
  prompt?: string;
  system_message?: string;
  model?: string;
  fields?: string;
  displaySection?: string;
  instructionsTab?: string;
  llmOptionsTab?: string;
  languageMode?: string;
  fieldsTab?: string;
};

export type EditToolStore = {
  setField: SetField<EditToolStore>;
  activeTab: string;
  toolDetails: AdminToolResponse['toolDetails'];
  pristineState: Pick<AdminToolResponse['toolDetails'], 'tool' | 'published_config' | 'categories'>;
  configs: ExplicitToolConfig[];
  allCategories: AdminToolResponse['allCategories'];
  allLlms: AdminToolResponse['allLlms'];
  publishedByUsers: ImmutableMap<string, AdminToolResponse['publishedByUsers'][number][1]>;
  errors: EditToolErrorMap;
  updateTool: (tool: Partial<Tables<'tools'>>) => void;
  updateToolConfig: (toolConfig: Partial<ExplicitToolConfig>) => void;
  updateConfigs: (configs: ExplicitToolConfig[]) => void;
  updateCategories: (newCategories: Tables<'categories'>[]) => void;
  setIconCategoryId: (id: string | null) => void;
  reset: () => void;
  loadTool: (slug: string) => Promise<void>;
  hydrateToolData: (data: AdminToolResponse) => void;
  saveTool: (args: { publishing: boolean; changeNote?: string | null }) => Promise<boolean>;
  saveToolConfig: (args?: { publishing?: boolean; skipValidation?: boolean }) => Promise<boolean>;
  validate: () => void;
  addNewToolConfig: (name: string, sourceToolConfig: ExplicitToolConfig) => Promise<void>;
  publishToolConfig: (toolConfig: ExplicitToolConfig, changeNote?: string | null) => Promise<boolean>;
  archiveToolConfig: (toolConfig: ExplicitToolConfig) => Promise<void>;
  clearToolFields: () => void;
  isToolDetailsPristine: () => boolean;
  isToolPristine: () => boolean;
  isToolConfigPristine: () => boolean;
  updatePristineState: (
    tool: AdminToolResponse['toolDetails']['tool'],
    config: ExplicitToolConfig,
    categories: AdminToolResponse['toolDetails']['categories'],
  ) => void;
  isAssistantTool: () => boolean;
};

const defaultState = {
  activeTab: '1',
  toolDetails: emptyAdminToolDetails(),
  pristineState: {
    tool: emptyTool(),
    published_config: emptyToolConfig(),
    categories: [],
  },
  configs: [],
  allCategories: [],
  allLlms: [],
  publishedByUsers: ImmutableMap<string, AdminToolResponse['publishedByUsers'][number][1]>(),
  errors: {},
};

export const createEditToolStoreSlice = createStoreSlice('EditToolStoreData', defaultState, ({ set, get, setField }) => ({
  setField,
  reset: () => set(structuredClone(defaultState)),
  updateTool: (tool: Partial<Tables<'tools'>>) => {
    set((s) => ({ toolDetails: { ...s.toolDetails, tool: { ...s.toolDetails.tool, ...tool } } }));
    get().validate();
  },
  updateToolConfig: (toolConfig: Partial<ExplicitToolConfig>) => {
    set((s) => {
      const newPublishedConfig = { ...s.toolDetails.published_config, ...toolConfig };
      const updates = { toolDetails: { ...s.toolDetails, published_config: newPublishedConfig }, pristineState: s.pristineState };
      if (newPublishedConfig.id !== s.pristineState.published_config.id) {
        updates.pristineState = { ...s.pristineState, published_config: structuredClone(newPublishedConfig) };
      }
      return updates;
    });
    get().validate();
  },
  updateConfigs: (configs: ExplicitToolConfig[]) => {
    set({ configs: structuredClone(configs) });
    get().validate();
  },
  updateCategories: (categories) => {
    set((s) => {
      const iconCategoryId = resolveIconCategoryId(s.toolDetails, categories);
      return {
        toolDetails: {
          ...s.toolDetails,
          categories: structuredClone(categories),
          tool: { ...s.toolDetails.tool, icon_category_id: iconCategoryId },
        },
      };
    });
    get().validate();
  },
  setIconCategoryId: (id) => {
    set((s) => ({
      toolDetails: { ...s.toolDetails, tool: { ...s.toolDetails.tool, icon_category_id: id } },
    }));
    get().validate();
  },
  loadTool: async (slug: string) => {
    set({ toolDetails: structuredClone(defaultState.toolDetails) });
    const response = await fetch<AdminToolResponse>(`/api/admin/tools/${slug}`);
    const data = await response.json();
    // Resolve the effective icon category up front so that tool.icon_category_id
    // and pristineState agree from the start. We verify the stored id is still a valid
    // category (guards against stale ids from removed categories). When null or stale,
    // we fall back to the first assigned category — one-time null→id write on next save, intentional.
    const effectiveIconCategoryId =
      data.toolDetails.categories.find((c) => c.id === data.toolDetails.tool.icon_category_id)?.id ??
      data.toolDetails.categories[0]?.id ??
      null;
    const toolWithEffectiveIcon = { ...data.toolDetails.tool, icon_category_id: effectiveIconCategoryId };
    set({
      toolDetails: { ...structuredClone(data.toolDetails), tool: toolWithEffectiveIcon },
      configs: data.configs,
      allCategories: data.allCategories,
      allLlms: data.allLlms,
      publishedByUsers: ImmutableMap(data.publishedByUsers),
    });
    get().updatePristineState(toolWithEffectiveIcon, data.toolDetails.published_config, data.toolDetails.categories);
    get().validate();
  },
  hydrateToolData: (data: AdminToolResponse) => {
    const effectiveIconCategoryId =
      data.toolDetails.categories.find((c) => c.id === data.toolDetails.tool.icon_category_id)?.id ??
      data.toolDetails.categories[0]?.id ??
      null;
    const toolWithEffectiveIcon = { ...data.toolDetails.tool, icon_category_id: effectiveIconCategoryId };
    set({
      toolDetails: { ...structuredClone(data.toolDetails), tool: toolWithEffectiveIcon },
      configs: data.configs,
      allCategories: data.allCategories,
      allLlms: data.allLlms,
      publishedByUsers: ImmutableMap(data.publishedByUsers),
    });
    get().updatePristineState(toolWithEffectiveIcon, data.toolDetails.published_config, data.toolDetails.categories);
    get().validate();
  },
  saveTool: async ({ publishing, changeNote = null }) => {
    const { errors, toolDetails, pristineState } = get();
    if (errors.displaySection) {
      toast({ title: 'Fix all form errors before saving', status: 'error' });
      return false;
    }

    if (!toolDetails.tool.id) {
      toast({ title: 'No Tool loaded', status: 'error' });
      return false;
    }

    const configId = publishing ? toolDetails.published_config.id : pristineState.tool.published_config_id;
    if (!configId) {
      toast({ title: "Can't find config ID", status: 'error' });
      return false;
    }

    const request: AdminUpdateToolRequest = {
      tool: toolDetails.tool,
      categories: toolDetails.categories.map((c: Tables<'categories'>) => (typeof c === 'string' ? c : c.id)),
      published_config_id: configId,
      publishing,
      ...(publishing && { change_note: changeNote }),
    };
    const response = await fetch(`/api/admin/tools/${pristineState.tool.slug}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
      onSuccess: ({ router }) => {
        const updatedTool = { ...toolDetails.tool, published_config_id: configId };
        set((s) => ({ toolDetails: { ...s.toolDetails, tool: updatedTool } }));
        get().updatePristineState(updatedTool, toolDetails.published_config, toolDetails.categories);
        toast({ title: 'Tool saved', status: 'success' });
        router.push(`/admin/tools/${toolDetails.tool.slug}`);
      },
      responseErrorHandlers: { conflict: toastConflict },
    });

    return response.ok;
  },
  updatePristineState: (tool, config, categories) => {
    set({
      pristineState: {
        tool: structuredClone(tool),
        published_config: structuredClone(config),
        categories: structuredClone(categories),
      },
    });
  },
  saveToolConfig: async (args) => {
    const { publishing = false, skipValidation = false } = args || {};
    const { errors, toolDetails, updateConfigs, configs } = get();

    if (!toolDetails.tool.id) {
      toast({ title: 'No Tool loaded', status: 'error' });
      return false;
    }

    if (!skipValidation && (errors.instructionsTab || errors.llmOptionsTab || errors.fieldsTab)) {
      toast({ title: 'Fix all form errors before saving', status: 'error' });
      return false;
    }

    const response = await fetch<ExplicitToolConfig>(`/api/admin/tool-configs/${toolDetails.published_config.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...toolDetails.published_config, publishing }),
      responseErrorHandlers: { conflict: toastConflict },
    });
    if (!response.ok) return false;

    const updatedConfig = await response.json();
    get().updateToolConfig(updatedConfig);
    updateConfigs(configs.map((c) => (c.id === toolDetails.published_config.id ? updatedConfig : c)));
    get().updatePristineState(toolDetails.tool, updatedConfig, toolDetails.categories);
    return true;
  },
  validate: () => {
    const { toolDetails } = get();

    const validatePrompt = () => {
      const assistantType = toolDetails.tool.assistant_tool_type;
      if (!toolDetails.published_config?.prompt && !assistantType) return 'Prompt is required';

      const validFields = toolDetails.published_config?.fields?.map((f) => f.name).concat('locale'); // locale is always injected into prompts
      const includedFields = getBadgesFromPlainText(toolDetails.published_config?.prompt);
      const invalidField = includedFields.find((f) => !validFields.includes(f));
      return invalidField ? `Field "${invalidField}" is not valid` : '';
    };

    const validateSystemMessage = () => {
      const assistantType = toolDetails.tool.assistant_tool_type;
      if (!assistantType) return '';

      const systemMessage = toolDetails.published_config.system_message;
      if (!systemMessage && assistantType === 'preconfigured') return 'System message is required';

      return '';
    };

    const errors: EditToolErrorMap = {
      title: toolDetails.tool.title ? '' : 'Title is required',
      slug: toolDetails.tool.slug ? '' : 'Slug is required',
      system_message: validateSystemMessage(),
      prompt: validatePrompt(),
      model: toolDetails.published_config.model_id ? '' : 'Model is required',
      fields: validateToolConfigFields(toolDetails.published_config.fields),
      displaySection: '',
      instructionsTab: '',
      llmOptionsTab: '',
      languageMode: '',
      fieldsTab: '',
    };

    errors.displaySection = errors.title || errors.slug;
    errors.instructionsTab = errors.system_message || errors.prompt;
    errors.llmOptionsTab = errors.model;
    errors.fieldsTab = errors.fields;
    set({ errors });
  },
  addNewToolConfig: async (name: string, sourceToolConfig: ExplicitToolConfig) => {
    const { toolDetails, configs, updateConfigs, updateToolConfig, updatePristineState } = get();
    const payload: TablesInsert<'tool_configs'> = {
      name,
      archived: sourceToolConfig.archived,
      fields: sourceToolConfig.fields,
      frequency_penalty: sourceToolConfig.frequency_penalty,
      max_length: sourceToolConfig.max_length,
      model_id: sourceToolConfig.model_id,
      presence_penalty: sourceToolConfig.presence_penalty,
      prompt: sourceToolConfig.prompt,
      studio_mode_enabled: sourceToolConfig.studio_mode_enabled,
      system_message: sourceToolConfig.system_message,
      temperature: sourceToolConfig.temperature,
      top_p: sourceToolConfig.top_p,
      tool_id: toolDetails.tool.id,
      translation_mode: sourceToolConfig.translation_mode,
      web_search_enabled: sourceToolConfig.web_search_enabled,
      knowledge_base_enabled: sourceToolConfig.knowledge_base_enabled,
      use_standards_tool: sourceToolConfig.use_standards_tool,
      semantic_search_context: sourceToolConfig.semantic_search_context,
    };

    await fetch<CreateToolConfigResponse>(`/api/admin/tool-configs`, {
      method: 'POST',
      body: JSON.stringify(payload),
      onSuccess: async ({ response }) => {
        const newConfig = await response.json();
        updateConfigs([...configs, newConfig]);
        updateToolConfig(newConfig);
        updatePristineState(toolDetails.tool, newConfig, toolDetails.categories);
        toast({ title: 'Version saved', status: 'success' });
      },
      responseErrorHandlers: { conflict: toastConflict },
    });
  },
  publishToolConfig: async (targetConfig: ExplicitToolConfig, changeNote?: string | null) => {
    const { isToolPristine, configs, saveToolConfig, saveTool, updateToolConfig, updateConfigs } = get();
    if (!isToolPristine()) {
      const confirmation = confirm('The Display tab contains unsaved changes that WILL also be published. Do you want to continue?');
      if (!confirmation) return false;
    }

    const updatedToolConfig = { ...targetConfig, publish_date: new Date().toISOString() };
    updateToolConfig(updatedToolConfig);
    updateConfigs(configs.map((c) => (c.id === updatedToolConfig.id ? updatedToolConfig : c)));

    let success = await saveToolConfig({ publishing: true });
    if (!success) {
      updateToolConfig(targetConfig);
      updateConfigs(configs);
      return false;
    }

    success = await saveTool({ publishing: true, changeNote: changeNote ?? null });
    if (!success) return false;
    toast({ title: 'Version published', status: 'success' });
    return true;
  },
  archiveToolConfig: async (toolConfig) => {
    const { toolDetails, configs, pristineState, updateToolConfig, updateConfigs, saveToolConfig, updatePristineState } = get();

    if (pristineState.published_config.id === toolConfig.id) {
      toast({ title: 'You cannot archive the published version', status: 'error' });
      return;
    }

    const updatedToolConfig = { ...toolConfig, archived: true };
    updateToolConfig(updatedToolConfig);
    await saveToolConfig({ skipValidation: true });
    updateConfigs(configs.filter((c) => c.id !== toolConfig.id).concat(updatedToolConfig));

    if (toolConfig.id === toolDetails.published_config.id) {
      const publishedConfig = configs.find((c) => c.id === pristineState.tool.published_config_id);
      if (!publishedConfig) throw new Error("Couldn't find published config");
      updateToolConfig(publishedConfig);
      updatePristineState(toolDetails.tool, publishedConfig, toolDetails.categories);
    }
  },
  clearToolFields: () =>
    set({ toolDetails: structuredClone(defaultState.toolDetails), pristineState: structuredClone(defaultState.pristineState) }),
  isToolDetailsPristine: () => {
    const { toolDetails, pristineState } = get();
    return isEqual(pristineState, pick(toolDetails, 'tool', 'published_config', 'categories'));
  },
  isToolPristine: () => {
    const { toolDetails, pristineState } = get();
    return isEqual(pristineState.tool, toolDetails.tool) && isEqual(pristineState.categories, toolDetails.categories);
  },
  isToolConfigPristine: () => {
    const { toolDetails, pristineState } = get();
    return isEqual(pristineState.published_config, toolDetails.published_config);
  },
  isAssistantTool: () => {
    const { toolDetails } = get();
    return !!toolDetails.tool.assistant_tool_type;
  },
}));

export function validateToolConfigFields(fields?: Json) {
  if (!fields) return 'Missing fields array';
  if (!Array.isArray(fields)) return 'Fields must be an array';
  for (const field of fields as FormElementProps[]) {
    if (!field.name) return 'All fields must have a name';

    // NOTE: when a component property is not specified, the field is treated as a text field in the app
    const componentKey = (field.component ?? 'TextInput') as ComponentKey;
    const component = componentMapKeys.includes(componentKey);

    if (component === undefined) {
      return `Field "${field.name}" has invalid component. Valid components are: ${componentMapKeys.join(', ')}`;
    }

    if (field.component === 'Select') {
      const typedField = field as SelectProps;
      if (!typedField.options || !Array.isArray(typedField.options)) {
        return `Field "${field.name}" has missing or invalid options`;
      }
    }
  }
  return '';
}
